import { type CriterionOutcome, type TestResult } from '@lixionary-jira/domain';
import { useState } from 'react';
import { api } from '../../../api/client';
import { useAction } from '../../../api/hooks';
import { Button } from '../../../ui/Button';
import { ErrorAlert } from '../../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../../ui/forms';
import { Modal } from '../../../ui/Modal';
import type { ImplDetail } from './types';

export function EvidenceDialog({
  i,
  onClose,
  inv,
}: {
  i: ImplDetail;
  onClose: () => void;
  inv: unknown[][];
}) {
  const prev = i.evidence;
  const [summary, setSummary] = useState(prev?.summary ?? '');
  const [rev, setRev] = useState(prev?.sourceRevision ?? '');
  const [branch, setBranch] = useState(prev?.branch ?? '');
  const [pr, setPr] = useState(prev?.pullRequest ?? '');
  const [results, setResults] = useState(
    i.ticket.acceptanceCriteria.map(
      (_, idx) =>
        prev?.criteriaResults.find((r) => r.criterionIndex === idx) ?? {
          criterionIndex: idx,
          outcome: 'met' as CriterionOutcome,
          observation: '',
        },
    ),
  );
  const [tests, setTests] = useState(
    prev?.tests.map((t) => `${t.name} | ${t.command} | ${t.result}`).join('\n') ?? '',
  );
  const [limitations, setLimitations] = useState(prev?.knownLimitations ?? '');
  const [deploy, setDeploy] = useState(prev?.deploymentConsiderations ?? '');
  const [rollback, setRollback] = useState(prev?.rollbackConsiderations ?? '');
  const m = useAction(
    () =>
      api.post(`/projects/${i.project.key}/tickets/${i.ticket.number}/implementation/evidence`, {
        expectedVersion: i.ticket.version,
        evidence: {
          summary,
          sourceRevision: rev,
          branch: branch || null,
          pullRequest: pr || null,
          criteriaResults: results,
          tests: tests
            .split('\n')
            .map((l) => l.split('|').map((x) => x.trim()))
            .filter((p) => p[0])
            .map(([name, command, result]) => ({
              name: name!,
              command: command ?? '',
              result: (['passed', 'failed', 'not_run'].includes(result ?? '')
                ? result
                : 'passed') as TestResult,
            })),
          knownLimitations: limitations,
          deploymentConsiderations: deploy,
          rollbackConsiderations: rollback,
          contextVersions: Object.fromEntries(i.contextVersions.map((c) => [c.section, c.currentVersionId])),
        },
      }),
    { success: 'Evidence recorded', invalidate: inv as never, onSuccess: onClose },
  );
  return (
    <Modal
      title="Record implementation evidence"
      onClose={onClose}
      wide
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => m.mutate(undefined)} loading={m.isPending}>
            Save evidence
          </Button>
        </>
      }
    >
      <Field label="Implementation summary">
        {(p) => <Textarea {...p} rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} />}
      </Field>
      <div className="grid-2">
        <Field label="Exact source revision (commit SHA)">
          {(p) => <Input {...p} className="mono" value={rev} onChange={(e) => setRev(e.target.value)} />}
        </Field>
        <Field label="Branch" optional>
          {(p) => (
            <Input {...p} className="mono" value={branch} onChange={(e) => setBranch(e.target.value)} />
          )}
        </Field>
      </div>
      <Field label="Pull request" optional>
        {(p) => <Input {...p} value={pr} onChange={(e) => setPr(e.target.value)} />}
      </Field>
      {i.ticket.acceptanceCriteria.map((c, idx) => (
        <div key={idx} className="grid-2">
          <Field label={`Criterion ${idx + 1}: ${c}`}>
            {(p) => (
              <Select
                {...p}
                value={results[idx]!.outcome}
                onChange={(e) =>
                  setResults(
                    results.map((r, j) =>
                      j === idx ? { ...r, outcome: e.target.value as CriterionOutcome } : r,
                    ),
                  )
                }
              >
                <option value="met">Met</option>
                <option value="not_met">Not met</option>
                <option value="not_applicable">Not applicable</option>
              </Select>
            )}
          </Field>
          <Field label="Observation">
            {(p) => (
              <Input
                {...p}
                value={results[idx]!.observation}
                onChange={(e) =>
                  setResults(results.map((r, j) => (j === idx ? { ...r, observation: e.target.value } : r)))
                }
              />
            )}
          </Field>
        </div>
      ))}
      <Field label="Tests" hint="One per line: name | command | passed, failed or not_run">
        {(p) => <Textarea {...p} mono rows={4} value={tests} onChange={(e) => setTests(e.target.value)} />}
      </Field>
      <Field label="Known limitations" optional>
        {(p) => (
          <Textarea {...p} rows={2} value={limitations} onChange={(e) => setLimitations(e.target.value)} />
        )}
      </Field>
      <Field label="Deployment and verification considerations">
        {(p) => <Textarea {...p} rows={2} value={deploy} onChange={(e) => setDeploy(e.target.value)} />}
      </Field>
      <Field label="Rollback considerations">
        {(p) => <Textarea {...p} rows={2} value={rollback} onChange={(e) => setRollback(e.target.value)} />}
      </Field>
      <p className="small muted">Saving acknowledges the current context versions shown on this page.</p>
      <ErrorAlert error={m.error} />
    </Modal>
  );
}
